/**
 * Decision 0166 §3: Settings › Subscriptions and Project settings › Members, as pure functions over the overview the
 * cloud serves (`GET /api/workspaces/:ws/subscriptions`).
 *
 * - §3.3 a subscription's name: the owner's label, else the plan, else the provider;
 * - §3.1 where it is shared: Only me (Only Mira), Everyone, or the project names;
 * - §3.5 the list grouped by person (the viewer first), with this month's cost per person;
 * - §3.6 which subscriptions the viewer's own work uses in a project (their choice), and the can't-work sentence when
 *   none of the chosen ones has the CLI a run needs.
 *
 * Decision 0167: a subscription is an account (owner, provider, account); its connections (the Platform Runner,
 * machines) are details under it. Each account shows its plan limits, this month's usage and one sharing setting; the
 * choice per project names accounts.
 */
import type { SubscriptionAccount, SubscriptionLimit, SubscriptionsOverview } from "@minga/protocol"
import { usageLevel } from "@/features/runners/format"

/** The one-line cost explanation at the sharing choice (0166 §3.5). */
export const SHARED_COST_LINE = "Shared: your plan pays for others’ work in these projects."

/** "Claude Code", "Codex": a subscription's provider as people say it. */
export const cliOfAgent = (agent: string): string =>
  agent === "codex"
    ? "Codex"
    : agent === "antigravity"
      ? "Antigravity"
      : agent === "muse"
        ? "Muse Code"
        : "Claude Code"

const productOf = (agent: string): string =>
  agent === "codex" ? "Codex" : agent === "antigravity" ? "Antigravity" : agent === "muse" ? "Muse" : "Claude"

/** "Claude Max", "Codex Plus", "Claude subscription": the plan, or the provider when the plan is unknown. */
export const planName = (agent: string, plan: string | null | undefined): string => {
  if (!plan) return `${productOf(agent)} subscription`
  const tier = plan.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  return `${productOf(agent)} ${tier}`
}

type Named = Pick<SubscriptionAccount, "label" | "agent" | "plan">

/** Decision 0166 §3.3: the owner's label, else the plan or provider. */
export const subscriptionName = (e: Named): string => e.label?.trim() || planName(e.agent, e.plan)

/**
 * Re-walk 2026-10-28: a subscription with whose it is, without doubling up: "Your Claude Max", "Mira's Codex Plus", but
 * a label that already says whose it is stays as it is ("Mira's Max", "Max for Mira", "My Pro"). `ownerName`: the
 * owner's name as the page knows it (its first name is used; null: a former member).
 */
export const ownedName = (
  e: Named & Pick<SubscriptionAccount, "ownerUserId">,
  viewerId: string,
  ownerName: string | null,
): string => {
  const name = subscriptionName(e)
  const first = ownerName?.trim().split(/\s+/)[0] ?? ""
  const label = e.label?.trim() ?? ""
  const escaped = first.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const saysWhose =
    label !== "" &&
    (/\S['’]s?(\s|$)/.test(label) ||
      /\b(my|your|our)\b/i.test(label) ||
      (first !== "" && new RegExp(`\\b${escaped}\\b`, "i").test(label)))
  if (saysWhose) return name
  if (e.ownerUserId === viewerId) return `Your ${name}`
  if (!first) return `A former member’s ${name}`
  return `${first.endsWith("s") ? `${first}’` : `${first}’s`} ${name}`
}

/** Decision 0167 §2: a connection as a chip names it: "Platform Runner" or the machine's name. */
export const connectionName = (
  c: Pick<SubscriptionAccount["connections"][number], "kind" | "runnerName" | "id">,
): string => (c.kind === "platform" ? "Platform Runner" : (c.runnerName ?? c.id))

/**
 * Re-walk 2026-10-28, 0167: what tells two subscriptions with the same name apart: where it is connected (the Platform
 * Runner, machines) and its account, or a local login's profile there when the account is not known.
 */
export const subscriptionWhere = (e: Pick<SubscriptionAccount, "connections" | "account">): string => {
  const only = e.connections.length === 1 ? e.connections[0] : undefined
  const places =
    only?.kind === "local"
      ? `on ${only.runnerName ?? "a machine"}`
      : e.connections.map(connectionName).join(", ")
  return [
    places,
    // a local login without a known account: its profile on that machine ("codex-work")
    e.account ?? (only?.kind === "local" ? only.id : null),
  ]
    .filter((x): x is string => x !== null && x !== "")
    .join(" · ")
}

/**
 * Decision 0166 §3.1: where a subscription is shared, as the overview shows it. `projectName` names the projects the
 * viewer sees; the others count as private projects.
 */
export const sharingText = (
  e: Pick<SubscriptionAccount, "scope" | "projectIds" | "ownerUserId">,
  viewerId: string,
  ownerName: string,
  projectName: (id: string) => string | null,
): string => {
  if (e.scope === "team") return "Everyone"
  if (e.scope === "personal" || e.projectIds.length === 0)
    return e.ownerUserId === viewerId ? "Only me" : `Only ${ownerName}`
  const names = e.projectIds.map(projectName)
  const known = names.filter((n): n is string => n !== null)
  const hidden = names.length - known.length
  return [
    ...known,
    ...(hidden > 0 ? [hidden === 1 ? "1 private project" : `${hidden} private projects`] : []),
  ].join(", ")
}

/** "Only me" / "Everyone" / "Some projects", for the sharing note. */
const scopeWord = (scope: SubscriptionAccount["scope"]): string =>
  scope === "team" ? "Everyone" : scope === "projects" ? "Some projects" : "Personal"

/**
 * Decision 0167 §4: the owner's note after the first read merged differing sharing: "Sharing was different on
 * jannik-mbp; now Personal everywhere". Null without a note.
 */
export const sharingNoteText = (e: Pick<SubscriptionAccount, "sharingNote" | "scope">): string | null =>
  e.sharingNote && e.sharingNote.connections.length > 0
    ? `Sharing was different on ${e.sharingNote.connections.join(", ")}; now ${scopeWord(e.scope)} everywhere`
    : null

export interface PersonGroup {
  readonly ownerUserId: string
  readonly name: string
  readonly mine: boolean
  /** This month so far at list prices (micro-USD), null when the ledger has nothing for them. */
  readonly costMicroUsd: number | null
  readonly accounts: ReadonlyArray<SubscriptionAccount>
  /** Decision 0167 §2: runs on their plans whose connection is gone ("Earlier connections"); null when none. */
  readonly earlier: { readonly runs: number; readonly listMicroUsd: number } | null
}

/** Decision 0166 §3.5, 0167 §2: one group per person, the viewer first, then by name; accounts by name. */
export const groupByPerson = (
  overview: Pick<SubscriptionsOverview, "accounts" | "costs"> &
    Partial<Pick<SubscriptionsOverview, "earlier">>,
  viewerId: string,
  nameOf: (userId: string) => string | null,
): ReadonlyArray<PersonGroup> => {
  const by = new Map<string, SubscriptionAccount[]>()
  for (const e of overview.accounts) by.set(e.ownerUserId, [...(by.get(e.ownerUserId) ?? []), e])
  return [...by]
    .map(([ownerUserId, accounts]): PersonGroup => {
      const cost = overview.costs.find((c) => c.ownerUserId === ownerUserId)
      const earlier = overview.earlier?.find((c) => c.ownerUserId === ownerUserId)
      return {
        ownerUserId,
        name: ownerUserId === viewerId ? "You" : (nameOf(ownerUserId) ?? "A former member"),
        mine: ownerUserId === viewerId,
        costMicroUsd: cost && cost.listMicroUsd > 0 ? cost.listMicroUsd : null,
        accounts: [...accounts].sort(
          (a, b) => subscriptionName(a).localeCompare(subscriptionName(b)) || a.key.localeCompare(b.key),
        ),
        earlier:
          earlier && earlier.runs > 0 ? { runs: earlier.runs, listMicroUsd: earlier.listMicroUsd } : null,
      }
    })
    .sort((a, b) => (a.mine === b.mine ? a.name.localeCompare(b.name) : a.mine ? -1 : 1))
}

/**
 * Re-walk 2026-10-28: a person's line under their name: this month's work on their plans at API prices ("This month:
 * about $12 on your plans, at API prices"), or that there is none yet.
 */
export const groupCostText = (
  group: Pick<PersonGroup, "mine" | "costMicroUsd">,
  money: (microUsd: number) => string,
): string =>
  group.costMicroUsd === null
    ? "This month: no work on these plans yet"
    : `This month: ${money(group.costMicroUsd)} on ${group.mine ? "your" : "their"} plans, at API prices`

// #region limits
/** Decision 0167 §2: a window's name: "5-hour", "Daily", "Weekly", "Weekly · Opus". */
export const limitLabel = (w: Pick<SubscriptionLimit, "window" | "model" | "modelTag">): string => {
  const base =
    w.window === "5h" ? "5-hour" : w.window === "24h" ? "Daily" : w.window === "7d" ? "Weekly" : "Limit"
  const model = w.modelTag ?? w.model
  return model ? `${base} · ${model.charAt(0).toUpperCase()}${model.slice(1)}` : base
}

/** "Fri 14:00": when a window resets (`fmt` formats a time; the page passes the viewer's locale). */
export const resetText = (
  resetsAt: number | null,
  now: number,
  fmt: (at: number) => string,
): string | null => (resetsAt === null ? null : resetsAt <= now ? "resets now" : `resets ${fmt(resetsAt)}`)

/**
 * Decision 0167 §2: the window near or at its limit, called out ("Weekly limit 92% used, resets Fri 14:00"); the most
 * used one at 90 % or more. Null when none is.
 */
export const limitCallout = (
  limits: ReadonlyArray<SubscriptionLimit>,
  now: number,
  fmt: (at: number) => string,
): string | null => {
  const top = [...limits]
    .filter((w) => usageLevel(w.usedPercent) === "high")
    .sort((a, b) => b.usedPercent - a.usedPercent)[0]
  if (!top) return null
  const resets = resetText(top.resetsAt, now, fmt)
  return `${limitLabel(top)} limit ${Math.round(top.usedPercent)}% used${resets ? `, ${resets}` : ""}`
}

/** "as of 5 min ago": the newest observation among the limits shown; null when none has a time. */
export const limitsAsOf = (
  limits: ReadonlyArray<Pick<SubscriptionLimit, "observedAt">>,
  relative: (d: Date) => string,
): string | null => {
  const at = limits.reduce<number | null>(
    (a, w) => (w.observedAt === null ? a : a === null || w.observedAt > a ? w.observedAt : a),
    null,
  )
  return at === null ? null : `as of ${relative(new Date(at))}`
}

/** What shows when an account has no limits yet. */
export const NO_LIMITS_TEXT = "Limits show after its next run"
// #endregion limits

// #region usage
/** "3 runs", "1 run". */
export const runsText = (n: number): string => `${n} ${n === 1 ? "run" : "runs"}`

/** Decision 0167 §2: "This month: 14 runs, ≈ $12.40 at API prices", or that nothing ran on it yet. */
export const accountUsageText = (
  u: Pick<SubscriptionAccount["usage"], "runs" | "listMicroUsd">,
  money: (microUsd: number) => string,
): string =>
  u.runs === 0 && u.listMicroUsd === 0
    ? "This month: nothing ran on it yet"
    : `This month: ${runsText(u.runs)}, ${money(u.listMicroUsd)} at API prices`

/** Whose work an `askedBy` key was: "You", a person's or agent's name, "Someone". */
export const askerName = (
  askedBy: string,
  viewerId: string,
  person: (id: string) => string | null,
  agent: (id: string) => string | null,
): string => {
  if (askedBy === `user:${viewerId}`) return "You"
  if (askedBy.startsWith("user:")) return person(askedBy.slice(5)) ?? "A former member"
  if (askedBy.startsWith("agent:")) return agent(askedBy.slice(6)) ?? "An agent"
  return "Someone"
}
// #endregion usage

/** Is this subscription shared with other people's work in this project (0166 §3.2)? */
export const sharedInProject = (
  e: Pick<SubscriptionAccount, "scope" | "projectIds">,
  projectId: string,
): boolean => e.scope === "team" || (e.scope === "projects" && e.projectIds.includes(projectId))

/** Project settings › Members: "Subscriptions shared here" (others' work in this project may use them). */
export const sharedHere = <A extends Pick<SubscriptionAccount, "scope" | "projectIds">>(
  accounts: ReadonlyArray<A>,
  projectId: string,
): ReadonlyArray<A> => accounts.filter((e) => sharedInProject(e, projectId))

/** Decision 0166 §3.6: what the viewer's work in a project may use: their own, and others' shared into it. */
export const usableIn = <A extends Pick<SubscriptionAccount, "ownerUserId" | "scope" | "projectIds">>(
  accounts: ReadonlyArray<A>,
  viewerId: string,
  projectId: string,
): ReadonlyArray<A> => accounts.filter((e) => e.ownerUserId === viewerId || sharedInProject(e, projectId))

/**
 * Decision 0166 §3.6: the viewer's choice in a project, null when they chose nothing (all). Decision 0167 §6: the
 * cloud names accounts; each chosen account's connection keys are added, so checks that read connections (the
 * Platform Runner's subscriptions in the lead's default and reach) agree with the hub.
 */
export const choiceIn = (
  overview: Pick<SubscriptionsOverview, "chosen"> & Partial<Pick<SubscriptionsOverview, "accounts">>,
  projectId: string,
): ReadonlyArray<string> | null => {
  const keys = overview.chosen[projectId]
  if (!keys || keys.length === 0) return null
  const out = new Set(keys)
  for (const a of overview.accounts ?? [])
    if (keys.includes(a.key)) for (const c of a.connections) out.add(c.key)
  return [...out]
}

/** Is this subscription in use for the viewer's work there (everything is, when nothing was chosen)? */
export const isChosen = (choice: ReadonlyArray<string> | null, key: string): boolean =>
  choice === null || choice.includes(key)

/**
 * Decision 0166 §3.6: the choice after the viewer ticks or unticks one subscription. Every usable one ticked is the
 * same as nothing chosen (null), so a subscription shared later is used too; unticking the last leaves an empty list,
 * which the caller refuses ("keep at least one").
 */
export const toggleChoice = (
  choice: ReadonlyArray<string> | null,
  usable: ReadonlyArray<Pick<SubscriptionAccount, "key">>,
  key: string,
): ReadonlyArray<string> | null => {
  const all = usable.map((e) => e.key)
  const current = (choice ?? all).filter((k) => all.includes(k))
  const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
  return all.every((k) => next.includes(k)) ? null : next
}

/**
 * Decision 0166 §3.6: the CLIs the viewer's chosen subscriptions offer in a project (every usable one's when nothing
 * was chosen). A CLI not in it is not offered for their runs there.
 */
export const chosenClis = (
  usable: ReadonlyArray<Pick<SubscriptionAccount, "key" | "agent">>,
  choice: ReadonlyArray<string> | null,
): ReadonlySet<string> => new Set(usable.filter((e) => isChosen(choice, e.key)).map((e) => e.agent))

/**
 * Decision 0166 §3.6, for the can't-work reason (build A's `setupFixes`): "none of your chosen subscriptions here has
 * Claude Code" when the viewer chose subscriptions in this project and none of them is for `agent`, but an unchosen
 * usable one is (so changing the choice fixes it). Null otherwise.
 */
export const notChosenReason = (
  usable: ReadonlyArray<Pick<SubscriptionAccount, "key" | "agent">>,
  choice: ReadonlyArray<string> | null,
  agent: string,
): string | null =>
  choice !== null && !chosenClis(usable, choice).has(agent) && usable.some((e) => e.agent === agent)
    ? `none of your chosen subscriptions here has ${cliOfAgent(agent)}`
    : null

/** "last used 3 days ago" or "not used yet". */
export const lastUsedText = (at: number | null, formatRelative: (d: Date) => string): string =>
  at === null ? "not used yet" : `last used ${formatRelative(new Date(at))}`

/** Decision 0167 §2: a connection chip's state: "online" or "last seen 2 h ago". */
export const connectionStateText = (
  c: Pick<SubscriptionAccount["connections"][number], "online" | "lastSeenAt">,
  formatRelative: (d: Date) => string,
): string =>
  c.online
    ? "online"
    : c.lastSeenAt === null
      ? "offline"
      : `last seen ${formatRelative(new Date(c.lastSeenAt))}`
